import { create } from 'zustand';
import { SpellSchool } from '../services/spellService';
import { Weapon, Armour, Creature, BaseType, BaseResource, TreasureItem, BuffOrDebuff, SpecialTag, PermanentInjury, Spell, SpellCategory } from '../types';
import { customCreatureService } from '../services/customCreatureService';
import { customWeaponService } from '../services/customWeaponService';
import { customArmourService } from '../services/customArmourService';
import { customBuffService } from '../services/customBuffService';
import { removeBuffReferences } from '../services/localStorageService';
import { queueSyncEntity } from '../services/syncCoordinator';

import weaponsData from '../data/weapons.json';
import armourData from '../data/armour.json';
import creaturesData from '../data/creatures.json';
import baseTypesData from '../data/baseTypes.json';
import baseResourcesData from '../data/baseResources.json';
import treasureItemsData from '../data/treasureItems.json';
import schoolsData from '../data/schools.json';
import buffsData from '../data/buffs.json';
import specialTagsData from '../data/specialTags.json';
import spellsData from '../data/spells.json';
import spellCategoriesData from '../data/spellCategories.json';
import injuriesData from '../data/injuries.json';

type RawWeapon = Omit<Weapon, 'specialTags'> & { specialTagIds: string[] };
type RawArmour = Omit<Armour, 'specialTags'> & { specialTagIds: string[] };
type RawCreature = Omit<Creature, 'weapons' | 'armourItems' | 'treasureItems'> & { weaponIds: string[]; armourIds: string[]; treasureItemIds: string[] };
type RawSpell = Omit<Spell, 'categories'> & { categoryIds: string[] };

function buildReferenceData() {
  const specialTagsMap = new Map<string, SpecialTag>(
    (specialTagsData as Array<{ id: string; description: string }>).map(t => [
      t.id,
      { id: t.id, description: t.description },
    ])
  );

  const weapons: Weapon[] = (weaponsData as unknown as RawWeapon[]).map(w => {
    const { specialTagIds, ...rest } = w;
    return { ...rest, specialTags: specialTagIds.map(id => specialTagsMap.get(id)!).filter(Boolean) };
  });
  const weaponsMap = new Map<string, Weapon>(weapons.map(w => [w.id, w]));

  const armours: Armour[] = (armourData as unknown as RawArmour[]).map(a => {
    const { specialTagIds, ...rest } = a;
    return { ...rest, specialTags: specialTagIds.map(id => specialTagsMap.get(id)!).filter(Boolean) };
  });
  const armoursMap = new Map<string, Armour>(armours.map(a => [a.id, a]));

  const categoryMap = new Map<string, SpellCategory>(
    (spellCategoriesData as Array<{ id: string; description: string }>).map(c => [
      c.id,
      { id: c.id, description: c.description },
    ])
  );

  const spells: Spell[] = (spellsData as unknown as RawSpell[]).map(s => {
    const { categoryIds, ...rest } = s;
    return { ...rest, categories: categoryIds.map(id => categoryMap.get(id)!).filter(Boolean) };
  });

  const spellNameMap = new Map<string, string>(spells.map(s => [s.id, s.name]));

  const treasureItems: TreasureItem[] = (treasureItemsData as TreasureItem[]).map(item => {
    if (item.description === null && item.invokedSpellId !== null) {
      const spellName = spellNameMap.get(item.invokedSpellId);
      if (spellName) {
        return { ...item, description: `A scroll containing the ${spellName} spell. When used, immediately casts this spell.` };
      }
    }
    return item;
  });
  const treasureItemsMap = new Map<string, TreasureItem>(treasureItems.map(t => [t.id, t]));

  const creatures: Creature[] = (creaturesData as unknown as RawCreature[]).map(c => {
    const { weaponIds, armourIds, treasureItemIds, ...rest } = c;
    return {
      ...rest,
      weapons: weaponIds.map(id => weaponsMap.get(id)!).filter(Boolean),
      armourItems: armourIds.map(id => armoursMap.get(id)!).filter(Boolean),
      treasureItems: (treasureItemIds ?? []).map(id => treasureItemsMap.get(id)!).filter(Boolean),
    };
  });

  return { weapons, armours, creatures, treasureItems, spells };
}

const resolved = buildReferenceData();
const initialCustomCreatures: Creature[] = customCreatureService.getAll().map((c) => ({ ...c, isCustom: true }));
const initialCustomWeapons: Weapon[] = customWeaponService.getAll().map((w) => ({ ...w, isCustom: true }));
const initialCustomArmours: Armour[] = customArmourService.getAll().map((a) => ({ ...a, isCustom: true }));
const initialCustomBuffs: BuffOrDebuff[] = customBuffService.getAll().map((buff) => ({ ...buff, isCustom: true }));

function schedulebestiarySync(id: string): void {
  queueSyncEntity(`custom-creature:${id}`);
}

function scheduleWeaponSync(id: string): void {
  queueSyncEntity(`custom-weapon:${id}`);
}

function scheduleArmourSync(id: string): void {
  queueSyncEntity(`custom-armour-item:${id}`);
}

function scheduleBuffsSync(id: string): void {
  queueSyncEntity(`custom-buff:${id}`);
}

interface ReferenceDataStore {
  weapons: Weapon[];
  armours: Armour[];
  creatures: Creature[];
  baseTypes: BaseType[];
  allBaseResources: BaseResource[];
  treasureItems: TreasureItem[];
  spells: Spell[];
  schools: SpellSchool[];
  buffs: BuffOrDebuff[];
  injuries: PermanentInjury[];
  specialTags: SpecialTag[];
  customCreatures: Creature[];
  customWeapons: Weapon[];
  customArmours: Armour[];
  customBuffs: BuffOrDebuff[];
  isLoading: boolean;
  isLoaded: boolean;
  error: string | null;
  loadReferenceData: (options?: { force?: boolean }) => Promise<void>;
  reloadCustomCreatures: () => void;
  reloadCustomWeapons: () => void;
  reloadCustomArmours: () => void;
  reloadCustomBuffs: () => void;
  getSpellById: (id: string) => Spell | undefined;
  getCreatureById: (id: string | null | undefined) => Creature | undefined;
  getCreaturesByType: (type: string) => Creature[];
  getBaseTypeById: (id: string | null | undefined) => BaseType | undefined;
  getSchoolNameById: (schoolId?: string | null) => string | undefined;
  addCustomCreature: (data: Omit<Creature, 'id' | 'isCustom'>) => Creature;
  updateCustomCreature: (id: string, data: Partial<Omit<Creature, 'id' | 'isCustom'>>) => void;
  deleteCustomCreature: (id: string) => void;
  addCustomWeapon: (data: Omit<Weapon, 'id' | 'isCustom'>) => Weapon;
  updateCustomWeapon: (id: string, data: Partial<Omit<Weapon, 'id' | 'isCustom'>>) => void;
  deleteCustomWeapon: (id: string) => void;
  addCustomArmour: (data: Omit<Armour, 'id' | 'isCustom'>) => Armour;
  updateCustomArmour: (id: string, data: Partial<Omit<Armour, 'id' | 'isCustom'>>) => void;
  deleteCustomArmour: (id: string) => void;
  addCustomBuff: (data: Omit<BuffOrDebuff, 'id' | 'isCustom'>) => BuffOrDebuff;
  updateCustomBuff: (id: string, data: Partial<Omit<BuffOrDebuff, 'id' | 'isCustom'>>) => void;
  deleteCustomBuff: (id: string) => void;
}

export const useReferenceDataStore = create<ReferenceDataStore>((set, get) => ({
  weapons: [...resolved.weapons, ...initialCustomWeapons],
  armours: [...resolved.armours, ...initialCustomArmours],
  customWeapons: initialCustomWeapons,
  customArmours: initialCustomArmours,
  customCreatures: initialCustomCreatures,
  customBuffs: initialCustomBuffs,
  creatures: [...resolved.creatures, ...initialCustomCreatures],
  baseTypes: baseTypesData as BaseType[],
  allBaseResources: baseResourcesData as BaseResource[],
  spells: resolved.spells,
  treasureItems: resolved.treasureItems,
  specialTags: (specialTagsData as Array<{ id: string; description: string }>).map(t => ({ id: t.id, description: t.description })),
  schools: (schoolsData as Array<{ id: string; school: string; description: string }>).map((s) => ({
    id: s.id,
    school: s.school,
    description: s.description,
  })),
  buffs: [...(buffsData as BuffOrDebuff[]), ...initialCustomBuffs],
  injuries: injuriesData as PermanentInjury[],
  isLoading: false,
  isLoaded: true,
  error: null,

  loadReferenceData: async (_options?) => {
    // Data is loaded synchronously from static JSON — nothing to fetch.
  },

  reloadCustomCreatures: () => {
    const fresh = customCreatureService.getAll().map((c) => ({ ...c, isCustom: true as const }));
    set({
      customCreatures: fresh,
      creatures: [...resolved.creatures, ...fresh],
    });
  },

  reloadCustomWeapons: () => {
    const fresh = customWeaponService.getAll().map((w) => ({ ...w, isCustom: true as const }));
    set({
      customWeapons: fresh,
      weapons: [...resolved.weapons, ...fresh],
    });
  },

  reloadCustomArmours: () => {
    const fresh = customArmourService.getAll().map((a) => ({ ...a, isCustom: true as const }));
    set({
      customArmours: fresh,
      armours: [...resolved.armours, ...fresh],
    });
  },

  reloadCustomBuffs: () => {
    const fresh = customBuffService.getAll().map((buff) => ({ ...buff, isCustom: true as const }));
    set({
      customBuffs: fresh,
      buffs: [...(buffsData as BuffOrDebuff[]), ...fresh],
    });
  },

  addCustomCreature: (data) => {
    const created = customCreatureService.create({ ...data, isCustom: true });
    const tagged: Creature = { ...created, isCustom: true };
    set((s) => ({
      customCreatures: [...s.customCreatures, tagged],
      creatures: [...s.creatures, tagged],
    }));
    schedulebestiarySync(created.id);
    return tagged;
  },

  updateCustomCreature: (id, data) => {
    const updated = customCreatureService.update(id, data);
    if (!updated) return;
    const tagged: Creature = { ...updated, isCustom: true };
    set((s) => ({
      customCreatures: s.customCreatures.map((c) => (c.id === id ? tagged : c)),
      creatures: s.creatures.map((c) => (c.id === id ? tagged : c)),
    }));
    schedulebestiarySync(id);
  },

  deleteCustomCreature: (id) => {
    customCreatureService.delete(id);
    set((s) => ({
      customCreatures: s.customCreatures.filter((c) => c.id !== id),
      creatures: s.creatures.filter((c) => c.id !== id),
    }));
    schedulebestiarySync(id);
  },

  addCustomWeapon: (data) => {
    const created = customWeaponService.create({ ...data, isCustom: true });
    const tagged: Weapon = { ...created, isCustom: true };
    set((s) => ({
      customWeapons: [...s.customWeapons, tagged],
      weapons: [...s.weapons, tagged],
    }));
    scheduleWeaponSync(created.id);
    return tagged;
  },

  updateCustomWeapon: (id, data) => {
    const updated = customWeaponService.update(id, data);
    if (!updated) return;
    const tagged: Weapon = { ...updated, isCustom: true };
    set((s) => ({
      customWeapons: s.customWeapons.map((w) => (w.id === id ? tagged : w)),
      weapons: s.weapons.map((w) => (w.id === id ? tagged : w)),
    }));
    scheduleWeaponSync(id);
  },

  deleteCustomWeapon: (id) => {
    customWeaponService.delete(id);
    set((s) => ({
      customWeapons: s.customWeapons.filter((w) => w.id !== id),
      weapons: s.weapons.filter((w) => w.id !== id),
    }));
    scheduleWeaponSync(id);
  },

  addCustomArmour: (data) => {
    const created = customArmourService.create({ ...data, isCustom: true });
    const tagged: Armour = { ...created, isCustom: true };
    set((s) => ({
      customArmours: [...s.customArmours, tagged],
      armours: [...s.armours, tagged],
    }));
    scheduleArmourSync(created.id);
    return tagged;
  },

  updateCustomArmour: (id, data) => {
    const updated = customArmourService.update(id, data);
    if (!updated) return;
    const tagged: Armour = { ...updated, isCustom: true };
    set((s) => ({
      customArmours: s.customArmours.map((a) => (a.id === id ? tagged : a)),
      armours: s.armours.map((a) => (a.id === id ? tagged : a)),
    }));
    scheduleArmourSync(id);
  },

  deleteCustomArmour: (id) => {
    customArmourService.delete(id);
    set((s) => ({
      customArmours: s.customArmours.filter((a) => a.id !== id),
      armours: s.armours.filter((a) => a.id !== id),
    }));
    scheduleArmourSync(id);
  },

  addCustomBuff: (data) => {
    const created = customBuffService.create({ ...data, isCustom: true });
    const tagged: BuffOrDebuff = { ...created, isCustom: true };
    set((state) => ({
      customBuffs: [...state.customBuffs, tagged],
      buffs: [...state.buffs, tagged],
    }));
    scheduleBuffsSync(created.id);
    return tagged;
  },

  updateCustomBuff: (id, data) => {
    const updated = customBuffService.update(id, data);
    if (!updated) return;
    const tagged: BuffOrDebuff = { ...updated, isCustom: true };
    set((state) => ({
      customBuffs: state.customBuffs.map((buff) => (buff.id === id ? tagged : buff)),
      buffs: state.buffs.map((buff) => (buff.id === id ? tagged : buff)),
    }));
    scheduleBuffsSync(id);
  },

  deleteCustomBuff: (id) => {
    removeBuffReferences(id);
    customBuffService.delete(id);
    set((state) => ({
      customBuffs: state.customBuffs.filter((buff) => buff.id !== id),
      buffs: state.buffs.filter((buff) => buff.id !== id),
    }));
    scheduleBuffsSync(id);
  },

  getSpellById: (id) => {
    return get().spells.find((s) => s.id === id);
  },

  getCreatureById: (id) => {
    if (!id) return undefined;
    return get().creatures.find((c) => c.id === id);
  },

  getCreaturesByType: (type) => {
    return get().creatures.filter((c) => c.type === type);
  },

  getBaseTypeById: (id) => {
    if (!id) return undefined;
    return get().baseTypes.find((b) => b.id === id);
  },

  getSchoolNameById: (schoolId) => {
    if (!schoolId) return undefined;
    const normalizedSchoolId = schoolId.trim().toLowerCase();
    const school = get().schools.find(
      (item) => item.id.trim().toLowerCase() === normalizedSchoolId
    );
    return school?.school;
  },
}));

